// useOutlineEditor — outline editing state machine extracted from outline-review.tsx.
//
// Manages: chapter CRUD, concept CRUD, expansion state, validation, feedback.
// Follows the useCourseIngest pattern (Session 47): callback-ref for stable callbacks.
//
// Session 48: View monolith decomposition — DEBT-19BI-VIEW-HOOKS.

import { useCallback, useMemo, useState } from 'react';
import type {
  CurriculumOutline,
  ChapterAnchor,
  ConceptAnchor,
} from '@examforge/shared/schemas';

// ─── Types ───────────────────────────────────────────────────────────────────

export type FeedbackIssue = 'missing_concept' | 'wrong_structure' | 'wrong_chapter' | 'other';

export interface OutlineEditorState {
  /** Deep-cloned, mutable outline (user edits are local until confirm). */
  outline: CurriculumOutline;
  /** Normalized outline — all chapters default to importance=2 if not set. */
  normalized: CurriculumOutline;
  /** Set of expanded chapter IDs. */
  expanded: Set<string>;
  /** Keyboard-focused chapter index. */
  focusedIdx: number;
  /** Whether the feedback panel is visible. */
  showFeedback: boolean;
  /** Whether feedback was submitted (shows confirmation state). */
  feedbackSent: boolean;
  /** Chapters are valid: ≥1 chapter, each has title + ≥1 concept. */
  isValid: boolean;
  /** Total concept count across all chapters. */
  totalConcepts: number;

  // ── Actions ──────────────────────────────────────────────────────────────
  toggleChapter: (id: string) => void;
  setFocusedIdx: (updater: (prev: number) => number) => void;
  setShowFeedback: (v: boolean) => void;
  collapseAll: () => void;
  updateChapter: (idx: number, patch: Partial<ChapterAnchor>) => void;
  deleteChapter: (idx: number) => void;
  updateConcept: (chIdx: number, conceptIdx: number, patch: Partial<ConceptAnchor>) => void;
  handleConfirm: () => void;
  handleFeedback: (issue: FeedbackIssue, detail: string) => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

export const DEFAULT_IMPORTANCE = 2;

// ─── Pure helpers (exported for testing) ──────────────────────────────────────

/** Deep-clone an outline to isolate user edits from props. */
export function cloneOutline(o: CurriculumOutline): CurriculumOutline {
  return JSON.parse(JSON.stringify(o)) as CurriculumOutline;
}

/** Normalize chapter importance — defaults to DEFAULT_IMPORTANCE if unset. */
export function normalizeOutline(o: CurriculumOutline): CurriculumOutline {
  return {
    ...o,
    chapters: o.chapters.map((ch) => ({
      ...ch,
      importance: ch.importance ?? DEFAULT_IMPORTANCE,
    })),
  };
}

/** Validate outline: ≥1 chapter, each has non-empty title + ≥1 concept. */
export function isValidOutline(o: CurriculumOutline): boolean {
  return (
    o.chapters.length >= 1 &&
    o.chapters.every((ch) => ch.title.trim().length > 0 && ch.concepts.length >= 1)
  );
}

/** Count total concepts across all chapters. */
export function countConcepts(o: CurriculumOutline): number {
  return o.chapters.reduce((sum, ch) => sum + ch.concepts.length, 0);
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useOutlineEditor(
  initialOutline: CurriculumOutline,
  onConfirm: (outline: CurriculumOutline) => void,
): OutlineEditorState {
  const [outline, setOutline] = useState<CurriculumOutline>(
    () => cloneOutline(initialOutline),
  );

  const normalized = useMemo(() => normalizeOutline(outline), [outline]);

  const [expanded, setExpanded] = useState<Set<string>>(() =>
    new Set(normalized.chapters.length > 0 ? [normalized.chapters[0]!.id] : []),
  );

  const [focusedIdx, setFocusedIdx] = useState(-1);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);

  // ── Chapter CRUD ────────────────────────────────────────────────────────

  const collapseAll = useCallback(() => {
    setExpanded(new Set());
    setFocusedIdx(-1);
  }, []);

  const toggleChapter = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const updateChapter = useCallback((idx: number, patch: Partial<ChapterAnchor>) => {
    setOutline((prev) => ({
      ...prev,
      chapters: prev.chapters.map((ch, i) => (i === idx ? { ...ch, ...patch } : ch)),
    }));
  }, []);

  const deleteChapter = useCallback((idx: number) => {
    setOutline((prev) => ({
      ...prev,
      chapters: prev.chapters.filter((_, i) => i !== idx),
    }));
  }, []);

  const updateConcept = useCallback(
    (chIdx: number, conceptIdx: number, patch: Partial<ConceptAnchor>) => {
      setOutline((prev) => ({
        ...prev,
        chapters: prev.chapters.map((ch, i) => {
          if (i !== chIdx) return ch;
          return {
            ...ch,
            concepts: ch.concepts.map((c, j) =>
              j === conceptIdx ? { ...c, ...patch } : c,
            ),
          };
        }),
      }));
    },
    [],
  );

  // ── Derived values ──────────────────────────────────────────────────────

  const isValid = useMemo(() => isValidOutline(normalized), [normalized]);
  const totalConcepts = useMemo(() => countConcepts(normalized), [normalized]);

  // ── Confirmation + feedback ─────────────────────────────────────────────

  const handleConfirm = useCallback(() => {
    onConfirm({ ...normalized, generatedAt: new Date().toISOString() });
  }, [normalized, onConfirm]);

  const handleFeedback = useCallback((_issue: FeedbackIssue, _detail: string) => {
    // TODO: wire to telemetry / feedback endpoint
    setFeedbackSent(true);
    setShowFeedback(false);
  }, []);

  return {
    outline,
    normalized,
    expanded,
    focusedIdx,
    showFeedback,
    feedbackSent,
    isValid,
    totalConcepts,
    toggleChapter,
    setFocusedIdx,
    setShowFeedback,
    collapseAll,
    updateChapter,
    deleteChapter,
    updateConcept,
    handleConfirm,
    handleFeedback,
  };
}
